import { useCallback, useEffect, useState } from "react";

/**
 * Persists a string setting in localStorage and keeps it in sync across
 * components via a custom window event.
 *
 * On mount the hook reads the stored value (falling back to `defaultValue`).
 * Calling the returned setter writes the value to localStorage and dispatches
 * the named event so that every other mounted instance of the hook with the
 * same `key` and `eventName` updates automatically.
 *
 * @param key - The localStorage key under which the value is stored.
 * @param defaultValue - The value to use when nothing is stored under `key`.
 * @param eventName - The window event name used to broadcast changes.
 * @returns A stateful tuple of `[currentValue, setter]`, matching the
 *   `useState` signature.
 */
export function usePersistedSetting(
    key: string,
    defaultValue: string,
    eventName: string
): [string, (next: string) => void] {
    const [value, setValue] = useState(
        () => localStorage?.getItem(key) ?? defaultValue
    );

    useEffect(() => {
        const onSync = () => {
            const stored = localStorage?.getItem(key) ?? defaultValue;
            setValue(prev => (prev === stored ? prev : stored));
        };
        window.addEventListener(eventName, onSync);
        return () => window.removeEventListener(eventName, onSync);
    }, [key, defaultValue, eventName]);

    const persist = useCallback(
        (next: string) => {
            setValue(next);
            localStorage?.setItem(key, next);
            window.dispatchEvent(new Event(eventName));
        },
        [key, eventName]
    );

    return [value, persist];
}
